import React, { JSX, useMemo } from "react";
import { Card, CardStyle } from "components/card/card";
import { EmptyText, EmptyTextSpacing } from "components/empty-text/empty-text";
import { EvaluationManager } from "components/evaluations/evaluation-manager/evaluation-manager";
import { EvaluationRecord } from "models/view-models/evaluations/evaluation-record";
import { Heading, HeadingSize } from "components/typography/heading/heading";
import { Icon } from "components/icons/icon";
import { IconSizes } from "components/icons/constants/icon-sizes";
import { Icons } from "components/icons/constants/icons";
import { NumberUtils } from "utilities/number-utils";
import { SubmittedEvaluation } from "components/evaluations/submitted-evaluation/submitted-evaluation";
import { useEnrollmentUnits } from "utilities/hooks/models/enrollments/use-enrollment-units";
import { useParams } from "react-router";
import {
    Paragraph,
    ParagraphSize,
    ParagraphStyle,
} from "components/typography/paragraph/paragraph";
import { useEnrollmentContext } from "utilities/contexts/use-enrollment-context";
import { t } from "utilities/localization/t";
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";
import { useRedirectOnForbidden } from "utilities/hooks/aspects/authorization/use-redirect-on-forbidden";
import { sitemap } from "sitemap";
import { useEvent } from "utilities/hooks/models/events/use-event";
import { EnrollmentScormPackageAssessmentStatus } from "models/enumerations/enrollments/enrollment-scorm-package-assessment-status";
import { useEnrollmentScormPackageAssessments } from "utilities/hooks/models/enrollments/use-enrollment-scorm-package-assessments";
import "./learner-online-learning-evaluation-page.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface LearnerOnlineLearningEvaluationPageProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "learner-online-learning-evaluation-page";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const LearnerOnlineLearningEvaluationPage: React.FC<LearnerOnlineLearningEvaluationPageProps> =
    validatePageAccess(AccessControlKeys.LearnerOnlineLearningEvaluationPage)((): JSX.Element => {
        useRedirectOnForbidden(sitemap.learner.learningAssignments.inProgress);
        const { enrollmentId: enrollmentIdParam } = useParams();
        const enrollmentId: number = useMemo(
            () => NumberUtils.parseInt(enrollmentIdParam) ?? 0,
            [enrollmentIdParam]
        );

        const { record: enrollment, setRecord: setEnrollment } = useEnrollmentContext();

        const isEvent = enrollment.eventId !== undefined && enrollment.eventId !== null;
        
        const { event } = useEvent({
            eventId: enrollment.event?.id ?? 0,
            includeEventSessions: true,
        });

        const lastEventDate = useMemo(() => {
            const endDate = event?.endDate() ?? event?.eventDays?.slice(-1)[0]?.eventDate();
            return endDate == null ? undefined : new Date(endDate);
        }, [event]);

        const { enrollmentUnits } = isEvent ? { enrollmentUnits: [] } : useEnrollmentUnits({
            enrollmentId,
        });

        const { enrollmentAssessments, isLoading } = isEvent ? { enrollmentAssessments: useEnrollmentScormPackageAssessments({ enrollmentId }).enrollmentScormPackageAssessments, isLoading: useEnrollmentScormPackageAssessments({ enrollmentId }).isLoading } 
            : { enrollmentAssessments: [], isLoading: false };

        const evaluationId = enrollment?.evaluation?.id ?? 0;

        const handleEvaluationSubmission = (evaluation: EvaluationRecord) => {
            const enrollmentWithChanges = enrollment.with({
                evaluation: evaluation,
                percentComplete: 100, // for OLL Evaluation is the last step
            });
            setEnrollment(enrollmentWithChanges);
        };

        const evaluationIsAvailable = (): boolean => {
            if (isEvent) {
                const assessmentsComplete = enrollmentAssessments?.every(
                    (assessment) => assessment?.status == EnrollmentScormPackageAssessmentStatus.Complete
                ) ?? false;

                return assessmentsComplete;
            }

            return enrollmentUnits.every(
                (enrollmentUnit) => enrollmentUnit.percentComplete === 100
            );
        };

        return (
            <div className={CSS_CLASS_NAME}>
                <div className={`${CSS_CLASS_NAME}__title`}>
                    <Icon type={Icons.Evaluation} size={IconSizes.Large} />
                    <Heading size={HeadingSize.Small}>{t("evaluation")}</Heading>
                </div>
                <Paragraph style={ParagraphStyle.Light} size={ParagraphSize.XLarge}>
                    {t(
                        "nfpaIsCommittedToProvidingYouWithHighqualityContentToHelpMeetYourLearningNeedsWeAppreciateYourFeedbackYourAnswersAndCommentsWillProvideValuableInputAsWeContinueToEnhanceOurOfferings"
                    )}
                </Paragraph>
                {!evaluationIsAvailable() && (
                    <Card style={CardStyle.Empty}>
                        <Icon type={Icons.Lock} size={IconSizes.Large} />
                        {!isEvent && (
                        <EmptyText spacing={EmptyTextSpacing.None}>
                            {t(
                                "theEvaluationWillBecomeAvailableFollowingTheCompletionOfTheSyllabus"
                            )}
                        </EmptyText>
                        )}
                        {isEvent && (
                            <EmptyText spacing={EmptyTextSpacing.None}>
                                {t("evaluationWillBeAvailableUponCompletionOfAllAssessments")}
                            </EmptyText>
                        )}
                    </Card>
                )}
                {evaluationIsAvailable() && evaluationId > 0 && (
                    <SubmittedEvaluation evaluationId={evaluationId} />
                )}
                {evaluationIsAvailable() && enrollmentId > 0 && evaluationId === 0 && (
                    <EvaluationManager
                        enrollmentId={enrollment?.id!}
                        evaluationTemplateId={enrollment.product?.evaluationTemplateId!}
                        handleEvaluationSubmission={handleEvaluationSubmission}
                    />
                )}
            </div>
        );
    });

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { LearnerOnlineLearningEvaluationPage };

// #endregion Exports
